import { http } from '@/api/client/httpClient';
import type {
  ClientProfitabilityFilterInput,
  ExpectedEodSwapSnapshotDto,
  InstrumentProfitabilityResultDto,
  LpSlippageSummaryDto,
  NopFiltersDto,
  RiskDashboardClientAccountsResultDto,
  RiskDashboardFilters,
} from '@/api/types/riskDashboard';

// Finkit.Tenants.User.RiskMonitoring.RiskDashboard.RiskDashboardAppService
//   POST /api/app/risk-dashboard/get-client-accounts-enrichment
// Finkit.Tenants.User.RiskMonitoring.ClientProfitability.ClientProfitabilityAppService
//   GET  /api/app/client-profitability/get-firm-net-pnl-chart-data
const RD_BASE = '/api/app/risk-dashboard';
const CP_BASE = '/api/app/client-profitability';

// Chart.js data shape returned by GetFirmNetPnlChartDataAsync.
export interface ChartJsDataset {
  label?: string | null;
  data: (number | null)[];
  borderColor?: string | null;
  backgroundColor?: string | null;
  fill?: boolean;
}
export interface ChartJsDataDto {
  labels: string[];
  datasets: ChartJsDataset[];
}

export const riskDashboardApi = {
  // ABP strips the "Get" verb prefix from the route and maps GetXxx -> HTTP GET.
  async getClientAccountsEnrichment(
    input: ClientProfitabilityFilterInput,
  ): Promise<RiskDashboardClientAccountsResultDto> {
    const { data } = await http.get<RiskDashboardClientAccountsResultDto>(
      `${RD_BASE}/client-accounts-enrichment`,
      { params: input },
    );
    return data;
  },

  async getFirmNetPnlChartData(input: ClientProfitabilityFilterInput): Promise<ChartJsDataDto> {
    const { data } = await http.get<ChartJsDataDto>(`${CP_BASE}/firm-net-pnl-chart-data`, {
      params: input,
    });
    return data;
  },

  // Instrument info-icon tooltip. GetDataAsync -> GET .../data (query filter).
  async getInstrumentProfitability(input: {
    instrument: string;
    isLive: boolean;
    startDate: string;
    endDate: string;
    connectorId?: number | null;
    login?: number | null;
    uniqueId?: string | null;
    showLp?: boolean;
  }): Promise<InstrumentProfitabilityResultDto> {
    const { data } = await http.get<InstrumentProfitabilityResultDto>(
      '/api/app/instrument-profitability/data',
      { params: input },
    );
    return data;
  },

  // Expected EOD Swap per-instrument breakdown (set filters.instrument to scope to one).
  async getExpectedEodSwap(filters: NopFiltersDto): Promise<ExpectedEodSwapSnapshotDto> {
    const { data } = await http.get<ExpectedEodSwapSnapshotDto>(`${RD_BASE}/expected-eod-swap`, {
      params: filters,
    });
    return data;
  },

  // LP login info-icon tooltip. GetSlippageDataAsync -> GET .../slippage-data.
  async getLpSlippageSummary(input: {
    startDate: string;
    endDate: string;
    dataSourceId: number;
  }): Promise<LpSlippageSummaryDto> {
    const { data } = await http.get<LpSlippageSummaryDto>('/api/app/execution-quality/slippage-data', {
      params: input,
    });
    return data;
  },
};

// ---- Filter mapping (mirrors risk-dashboard-filters.js) ----

export function toNopFiltersDto(f: RiskDashboardFilters): NopFiltersDto {
  return {
    login: f.login,
    uniqueId: f.uniqueId,
    platformId: f.platformId,
    ccyCurrency: false,
    instrument: f.instrument,
    mifidFilter: f.mifidFilter,
    groupName: f.groupName,
    groupTextFilter: f.groupTextFilter,
    portfolioId: f.portfolioId,
    requestedCurrency: null,
    symbolMapping: !!f.symbolMapping,
  };
}

export function toClientProfitabilityInput(f: RiskDashboardFilters): ClientProfitabilityFilterInput {
  return {
    startDate: f.startDate,
    endDate: f.endDate,
    dataSourceId: f.platformId ?? null,
    login: f.login ?? null,
    uniqueId: f.uniqueId ?? null,
    byUniqueId: !!f.byUniqueId,
    mifidClassification: (f.mifidFilter as ClientProfitabilityFilterInput['mifidClassification']) ?? null,
    executionVenue: (f.executionVenue as ClientProfitabilityFilterInput['executionVenue']) ?? null,
    group: f.groupName ?? null,
    groupText: f.groupTextFilter ?? null,
    groupProfile: f.groupProfile ?? null,
    ifSwaps: f.ifSwaps ?? null,
    countryId: f.countryId ?? null,
    portfolioId: f.portfolioId ?? null,
    liveData: !!f.liveMode,
    showLp: f.showLp !== false,
    maxResultCount: 50000,
    skipCount: 0,
  };
}

export function todayDateString(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function yesterdayDateString(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
